import { deactivateVisualGeomOriginPick } from "../../../runtime/visualGeomOriginPickState";
import { applyPosPickVisuals } from "../../../runtime/posPickVisualBridge";
import { logger } from "../../../utils/logger";

function getObjectPickIdentity(obj) {
  let cur = obj;
  while (cur) {
    const ud = cur.userData || {};
    if (ud.meshName || ud.bodyUuid || ud.geomUuid || ud.type === "visual") {
      return {
        meshUuid: ud.meshName ? String(ud.meshName) : null,
        bodyUuid: ud.bodyUuid ? String(ud.bodyUuid) : null,
        geomUuid: ud.geomUuid ? String(ud.geomUuid) : null,
        type: ud.type ? String(ud.type) : null,
      };
    }
    cur = cur.parent;
  }
  return { meshUuid: null, bodyUuid: null, geomUuid: null, type: null };
}

function matchesVisualGeomOriginPickState(obj, pickState) {
  if (
    !obj?.isMesh ||
    !pickState?.active ||
    pickState.mode !== "meshReference" ||
    !pickState.meshUuid ||
    !pickState.bodyUuid ||
    !pickState.geomUuid
  ) {
    return false;
  }
  const identity = getObjectPickIdentity(obj);
  if (identity.bodyUuid !== pickState.bodyUuid) return false;
  if (identity.geomUuid !== pickState.geomUuid) return false;
  if (identity.meshUuid !== pickState.meshUuid) return false;
  return identity.type === "visual" || !!identity.meshUuid;
}

function findRefTransformLayer(obj, pickState) {
  let cur = obj;
  while (cur) {
    const ud = cur.userData || {};
    if (
      ud.transformLayer === "ref" &&
      (!pickState.meshUuid || String(ud.meshName || "") === pickState.meshUuid) &&
      (!pickState.geomUuid || String(ud.geomUuid || "") === pickState.geomUuid)
    ) {
      return cur;
    }
    cur = cur.parent;
  }
  return null;
}

export function getVisualGeomOriginPickHit(intersects, pickState) {
  if (!Array.isArray(intersects) || !pickState?.active) return null;
  for (const intersect of intersects) {
    if (matchesVisualGeomOriginPickState(intersect?.object, pickState)) return intersect;
  }
  return null;
}

export function updateVisualGeomOriginPickMarker({ ensureMarker, hit }) {
  const marker = ensureMarker();
  if (!marker) return;
  if (!hit?.point) {
    marker.visible = false;
    return;
  }
  marker.position.copy(hit.point);
  marker.visible = true;
}

export function commitVisualGeomOriginPick({ hit, pickState }) {
  if (!hit?.point || !pickState?.active || !pickState.bodyUuid || !pickState.geomUuid) {
    return false;
  }

  const detail = {
    mode: pickState.mode,
    meshUuid: pickState.meshUuid,
    bodyUuid: pickState.bodyUuid,
    geomUuid: pickState.geomUuid,
    world: [hit.point.x, hit.point.y, hit.point.z],
  };

  const refGroup = findRefTransformLayer(hit.object, pickState);
  const refParent = refGroup?.parent || null;
  if (!refParent) {
    logger.warn("[visualGeomOriginPick] Could not resolve mesh reference transform layer.");
    return true;
  }
  const referencePosition = hit.point.clone();
  refParent.worldToLocal(referencePosition);
  detail.referencePosition = [referencePosition.x, referencePosition.y, referencePosition.z];

  window.dispatchEvent(new CustomEvent("visual-geom-origin-picked", { detail }));
  deactivateVisualGeomOriginPick();
  applyPosPickVisuals(false);
  return true;
}
